/* eslint-disable no-case-declarations */
/* eslint-disable import/prefer-default-export */
import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import mAuth from 'models/auth';
import useMutation from 'hooks/useMutation';
import { useSelector, useDispatch } from 'react-redux';
import _get from 'lodash/get';
import { useToast } from 'components/toast/Toast';

const INITIAL_FIELDS = {
  email: '',
};

const INITIAL_ERRORS = {
  email: null,
};

export const useRecoverPassword = ({
  handleFormTypeChange,
}) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch();

  const slugId = useSelector((state) => _get(state, 'cpmSlug.current.result.id', null));
  const { showToast, toast } = useToast();

  // 📝 Manage fields
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [submitStatus, setSubmitStatus] = useState(null);

  // 🔄 Sign in
  const query = mAuth.mutCreatePasswordRecoveryLink;
  const {
    exec: createPasswordRecoveryLink,
    loading: mutationLoading,
    errors: mutationErrors,
    data: mutationData,
  } = useMutation({
    query,
  });

  // 🔍 Validate fields
  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return t({ id: 'formError.email.cantBeBlank' });
        const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!REGEX_EMAIL.test(value)) return t({ id: 'formError.email.badFormat' });
        return '';
      default:
        return '';
    }
  }, []);

  // 📝 Validate form
  const validateForm = useCallback(() => {
    let newErrors = {};

    const errorEmail = validateField('email', fields.email);
    if (errorEmail) newErrors = { ...newErrors, email: errorEmail };

    setErrors(newErrors);
    const isValidated = Object.keys(newErrors).length === 0;
    return isValidated;
  }, [fields, validateField]);

  // 🔁 Change field
  const changeField = ({ name, value }) => {
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const resetFields = () => {
    setFields(INITIAL_FIELDS);
    setErrors(INITIAL_ERRORS);
  };

  const handleBlur = ({ name, value }) => {
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // 📬 Submit
  const handleSubmit = async () => {
    const isValidated = validateForm();
    if (!isValidated) return;

    setSubmitStatus(null);

    const variables = {
      input: {
        slugId,
        email: fields.email,
      },
    };

    // 🚀 Request
    await createPasswordRecoveryLink(variables);
  };

  // ✅ Manage success
  useEffect(() => {
    if (mutationData && mutationData.createPasswordRecoveryLink) {
      const STATUS = mutationData.createPasswordRecoveryLink;

      if (STATUS === 'OK') {
        resetFields();
        setSubmitStatus('success');
        dispatch({
          type: 'RECOVER_PASSWORD',
          status: 'GET_LINK',
        });
        handleFormTypeChange('signin');
      }

      if (STATUS === 'INVALID') {
        setErrors({
          email: t({ id: 'toast.recoverPassword.error' }),
        });
        setSubmitStatus('error');
      }
    }
  }, [mutationData]);

  /* 🔴 Errors */
  useEffect(() => {
    if (mutationErrors) {
      setSubmitStatus('error');

      // 🛜 Network error
      if (mutationErrors.network && !toast) {
        showToast({
          message: t({ id: 'formError.generateLinkRecoverPasswordGlobalError' }),
          type: 'alert',
        });
      }

      // 📝 Global form error
      if (mutationErrors.global) {
        setErrors({
          email: mutationErrors.global,
        });
      }
    }
  }, [_get(mutationErrors, 'global', null)]);

  return {
    fields,
    changeField,
    resetFields,
    submitStatus,
    setSubmitStatus,

    errors: { ...mutationErrors, ...errors },
    isSubmitting: mutationLoading,

    handleBlur,
    handleSubmit,
  };
};



// WEBPACK FOOTER //
// ./routes/login/components/cpm_manager/components/Recover/useRecoverPassword.js